import { useMemo, useState } from 'react';
import { useApiQuery } from '../../engine/api/hooks';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { Switch } from '../../engine/design/primitives/Input';
import { Badge, EmptyState, Skeleton, StatusBadge } from '../../engine/design/primitives/Misc';
import { AttachmentsPanel } from '../../engine/views/AttachmentsPanel';
import { formatDateTime } from '../../lib/format';
import type { ScreenContext } from '../../engine/screens/types';

// Email detail → "Message" tab. The stored HTML (already sanitised server-side) is rendered
// ONLY inside a fully sandboxed iframe (no scripts, no forms, no navigation, no same-origin)
// with a CSP that blocks every network fetch except, on request, https images. Text and the
// raw headers are plain panels; attachments reuse the platform panel.

const TONES: Record<string, 'info' | 'muted' | 'neg' | 'warn' | 'pos'> = { normal: 'info', auto_reply: 'muted', bounce: 'neg', out_of_office: 'warn', spam: 'neg', loop: 'neg' };

interface Raw {
  headers: Record<string, string | string[]>;
  text: string | null;
  html: string | null;
  attachments: { id: string; filename: string; mime: string; bytes: number }[];
}

export function frameDocument(html: string, remoteImages: boolean): string {
  const img = remoteImages ? 'cid: data: https:' : 'cid: data:';
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${img}; style-src 'unsafe-inline'; font-src 'none'"><style>body{margin:12px;font:14px/1.5 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1b1915;background:#fff;word-break:break-word}img{max-width:100%}blockquote{border-left:2px solid #ccc;margin:8px 0;padding-left:10px;color:#555}</style></head><body>${html}</body></html>`;
}

export function MessageView({ id, row }: ScreenContext) {
  const r = (row ?? {}) as Record<string, unknown>;
  const [view, setView] = useState<'html' | 'text' | 'headers'>(r['html_body'] ? 'html' : 'text');
  const [remote, setRemote] = useState(false);
  const raw = useApiQuery<Raw>(['email-message-raw', id], `/v1/email-messages/${id}/raw`, { enabled: Boolean(id) && view === 'headers' });
  const html = String(r['html_body'] ?? '');
  const text = String(r['text_body'] ?? '');
  const doc = useMemo(() => frameDocument(html, remote), [html, remote]);
  const to = (r['to_emails'] as string[] | undefined) ?? [];
  const cc = (r['cc_emails'] as string[] | undefined) ?? [];
  const cls = String(r['classification'] ?? 'normal');

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-5)' }}>
      <dl className="hf-kv" aria-label="Envelope">
        <div>
          <dt>From</dt>
          <dd>{r['from_name'] ? `${String(r['from_name'])} <${String(r['from_email'] ?? '')}>` : String(r['from_email'] ?? '—')}</dd>
        </div>
        <div>
          <dt>To</dt>
          <dd>{to.join(', ') || '—'}</dd>
        </div>
        {cc.length > 0 && (
          <div>
            <dt>Cc</dt>
            <dd>{cc.join(', ')}</dd>
          </div>
        )}
        <div>
          <dt>{r['direction'] === 'out' ? 'Sent' : 'Received'}</dt>
          <dd>{formatDateTime(String(r['direction'] === 'out' ? r['sent_at'] ?? r['created_at'] : r['received_at'] ?? r['created_at']))}</dd>
        </div>
        <div>
          <dt>Classification</dt>
          <dd className="hf-row" style={{ gap: 'var(--s-2)', flexWrap: 'wrap' }}>
            <Badge tone={TONES[cls] ?? 'muted'}>{cls.replace(/_/g, ' ')}</Badge>
            <StatusBadge status={String(r['status'] ?? '')} tones={{ processed: 'pos', received: 'info', ignored: 'muted', sent: 'pos', failed: 'neg' }} />
            {r['matched_by'] ? <Badge tone="muted">matched by {String(r['matched_by']).replace(/_/g, ' ')}</Badge> : null}
            {r['ignore_reason'] ? <span className="hf-muted">{String(r['ignore_reason'])}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Linked to</dt>
          <dd>{r['entity_type'] ? `${String(r['entity_type'])} ${String(r['entity_id'])}` : <span className="hf-muted">not linked</span>}</dd>
        </div>
      </dl>

      <div className="hf-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--s-3)' }}>
        <Tabs
          variant="pills"
          aria-label="Body view"
          value={view}
          onChange={(k) => setView(k as typeof view)}
          tabs={[
            { key: 'html', label: 'HTML', disabled: !html },
            { key: 'text', label: 'Text' },
            { key: 'headers', label: 'Headers' },
          ]}
        />
        {view === 'html' && <Switch checked={remote} onChange={(e) => setRemote(e.target.checked)} label="Load remote images" />}
      </div>

      {view === 'html' && (
        <iframe
          title="Email body"
          // No allow-* tokens at all: scripts, forms, popups, top-navigation and same-origin are all off.
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={doc}
          className="hf-card"
          style={{ width: '100%', minHeight: 420, border: '1px solid var(--rule)', background: '#fff' }}
        />
      )}
      {view === 'text' && (
        <pre className="hf-card pad" style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--font-sans)', fontSize: 'var(--t-13)', margin: 0 }} aria-label="Plain text body">
          {text || <span className="hf-muted">No text part.</span>}
        </pre>
      )}
      {view === 'headers' &&
        (raw.isLoading ? (
          <Skeleton lines={6} />
        ) : raw.data && Object.keys(raw.data.headers).length > 0 ? (
          <dl className="hf-kv" aria-label="Headers" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-12)' }}>
            {Object.entries(raw.data.headers).map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{Array.isArray(v) ? v.join('\n') : String(v)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <EmptyState title="No headers stored" icon="file-text" />
        ))}

      {id && r['has_attachments'] ? <AttachmentsPanel entityType="email_message" entityId={id} /> : null}
    </div>
  );
}
